import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff } from 'lucide-react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import './add.scss'; // Import the SCSS file
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { translateThis } from '../../../helpers/language.helper';
import { register } from '../../../services/api/auth';
import { showSnackbar } from '../../../components/snackbar/index';
import { isLoggedIn } from '../../../helpers/auth.helper';
import { getCurrentFacilityURL } from '../../../helpers/helper.functions';

const AddAdminUserModal = ({ isOpen, setIsOpen, refresh }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentFacilityURL = getCurrentFacilityURL();
  const { from } = location.state || { from: { pathname: `/${currentFacilityURL}/my-portal` } };

  // state variables for handling input fields values
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Permission options with keys and labels
  const permissionOptions = [
    { key: 'AI Agents', label: 'AI Agents', subOptions: [
      { key: 'Create Agent', label: 'Create Agent'},
      { key: 'See Agents', label: 'See Agents'},
      { key: 'Edit Agents', label: 'Edit Agents'},
      { key: 'Delete Agents', label: 'Delete Agents'},
    ]},
    { key: 'Tickets', label: 'Tickets', subOptions: [
      { key: 'Create Ticket', label: 'Create Ticket'},
      { key: 'See Tickets', label: 'See Tickets'},
      { key: 'Edit Tickets', label: 'Edit Tickets'},
      { key: 'Delete Tickets', label: 'Delete Tickets'},
      { key: 'Chat with user', label: 'Chat with user'},
      { key: 'Mark as completed', label: 'Mark as completed'},
    ]},
    { key: 'Dashboard', label: 'Dashboard', subOptions: [
      { key: 'View Dashboard', label: 'View Dashboard'},
    ]},
    { key: 'Admin Users', label: 'Admin Users', subOptions: [
      { key: 'Create Admin User', label: 'Create Admin User'},
      { key: 'See Admin Users', label: 'See Admin Users'},
      { key: 'Edit Admin Users', label: 'Edit Admin Users'},
      { key: 'Delete Admin Users', label: 'Delete Admin Users'},
    ]},
  ];

  // Validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string()
      .min(2, 'First name must be at least 2 characters')
      .required('First name is required'),
    lastName: Yup.string()
      .min(2, 'Last name must be at least 2 characters')
      .required('Last name is required'),
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required'),
    password: Yup.string()
      .min(8, 'Password must be at least 8 characters')
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, 'Password must contain at least one uppercase letter, one lowercase letter, and one number')
      .required('Password is required'),
  });

  /**
   * On submit the form
   */
  const createAdmin = (formData) => {
    setIsLoading(true);
    const postData = {
      "name": `${formData.firstName}`,
      "lastName": `${formData.lastName}`,
      "email": formData.email,
      "mobileNo": formData.phone,
      "password": formData.password,
      subAdmin: true,
      subAdminAccesses: formData.permissions // This will now be an array of permission keys
    }
    register(postData).then((data) => {
      if (data.success) {
        showSnackbar().success(data.message)
      } else {
        showSnackbar().failure(data.message);
      }
      navigate(0);
    }).finally(() => {
      setIsOpen(false);
      formik.resetForm();
      setIsLoading(false)
    });
  }

  const formik = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      phone: '',
      permissions: [] // Changed from object to array
    },
    validationSchema,
    onSubmit: (values) => {
      createAdmin(formik.values);
    }
  });

  // Handle permission toggle
  const handlePermissionChange = (permissionKey) => {
    const currentPermissions = [...formik.values.permissions];
    const index = currentPermissions.indexOf(permissionKey);

    if (index > -1) {
      // Remove permission if it exists
      currentPermissions.splice(index, 1);
    } else {
      // Add permission if it doesn't exist
      currentPermissions.push(permissionKey);
    }

    formik.setFieldValue('permissions', currentPermissions);
  };

  // Check if permission is selected
  const isPermissionSelected = (permissionKey) => {
    return formik.values.permissions.includes(permissionKey);
  };

  const handleCancel = () => {
    formik.resetForm();
    setIsOpen(false);
  };

  const getFieldError = (fieldName) => {
    return formik.touched[fieldName] && formik.errors[fieldName];
  };

  // Handle escape key press
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.keyCode === 27 && isOpen) {
        handleCancel();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleCancel();
    }
  };

  if (!isOpen) return null;

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div className="admin-modal-overlay" onClick={handleBackdropClick}>
      <div className="admin-modal">
        {/* Modal Header */}
        <div className="modal-header">
          <div className="header-left">
            <h2 className="modal-title" id="modal-title">Create New User</h2>
          </div>
          <button
            type="button"
            className="close-button"
            onClick={handleCancel}
          >
            <X />
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          <form onSubmit={formik.handleSubmit}>
            {/* Basic Information */}
            <div className="form-grid">
              <div className="form-field">
                <div className="label">First Name</div>
                <input
                  type="text"
                  name="firstName"
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`form-input ${getFieldError('firstName') ? 'error' : ''}`}
                  placeholder="Enter first name"
                />
                {getFieldError('firstName') && (
                  <div className="error-message">{formik.errors.firstName}</div>
                )}
              </div>

              <div className="form-field">
                <div className="label">Last Name</div>
                <input
                  type="text"
                  name="lastName"
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`form-input ${getFieldError('lastName') ? 'error' : ''}`}
                  placeholder="Enter last name"
                />
                {getFieldError('lastName') && (
                  <div className="error-message">{formik.errors.lastName}</div>
                )}
              </div>

              <div className="form-field">
                <div className="label">Email</div>
                <input
                  type="email"
                  name="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`form-input ${getFieldError('email') ? 'error' : ''}`}
                  placeholder="hello@example.com"
                />
                {getFieldError('email') && (
                  <div className="error-message">{formik.errors.email}</div>
                )}
              </div>

              <div className="form-field">
                <div className="password-field">
                  <div className="label">Password</div>
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`form-input ${getFieldError('password') ? 'error' : ''}`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff /> : <Eye />}
                  </button>
                </div>
                {getFieldError('password') && (
                  <div className="error-message">{formik.errors.password}</div>
                )}
              </div>

              <div className="form-field full-width">
                <input
                  type="tel"
                  name="phone"
                  value={formik.values.phone}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  className={`form-input ${getFieldError('phone') ? 'error' : ''}`}
                  placeholder="Enter phone number"
                />
                {getFieldError('phone') && (
                  <div className="error-message">{formik.errors.phone}</div>
                )}
              </div>
            </div>

            {/* Access Management */}
            <div className="permissions-section">
              <h3 className="section-title">Access Management</h3>
                {permissionOptions.map((accessModules) => (
                  <>
                  <div className="label"><u>{accessModules.label}</u></div>
              <div className="permissions-grid">
                {accessModules.subOptions.map((permission) => (
                  // eslint-disable-next-line jsx-a11y/no-static-element-interactions
                  <div
                    key={permission.key}
                    className="permission-item"
                    onClick={() => handlePermissionChange(permission.key)}
                  >
                    <div className="checkbox-wrapper">
                      <input
                        type="checkbox"
                        id={`permission-${permission.key}`}
                        checked={isPermissionSelected(permission.key)}
                        onChange={() => handlePermissionChange(permission.key)}
                        className="checkbox-input"
                      />
                      <div className={`checkbox-custom ${isPermissionSelected(permission.key) ? 'checked' : ''}`}>
                        <div className="checkmark">
                          <svg id="eye-check" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </div>
                      </div>
                    </div>
                    <label
                      htmlFor={`permission-${permission.key}`}
                      className="permission-label"
                    >
                      {permission.label}
                    </label>
                  </div>
                ))}
              </div>
                </>
              ))}
            </div>

            {/* Action Buttons */}
            <div className="action-buttons">
              <button
                type="button"
                onClick={handleCancel}
                className="btn btn-secondary"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddAdminUserModal;